import { useLiveSessions } from "@/hooks/call-center/useLiveSessions";
import { useWaitingRoomLeads } from "@/hooks/call-center/useWaitingRoomLeads";
import { useOtherAgentEngagements } from "@/hooks/call-center/useOtherAgentEngagements";
import useAuthUser from "@/hooks/useAuthUser";
import { useAppSelector } from "@/hooks/redux-hooks";
import { selectSelectedCallCenter, selectSelectionReady } from "@/store/call-centers";
import { TJoinedClient, TLiveSession } from "@/models/call.center.model";
import { getMyCurrentCallCenterStatus, notifyAgentStatusChanged } from "@/services/call-center/agents.service";
import { CALLCENTER_USER_STATUS_IN_CALL, CALLCENTER_USER_STATUS_READY } from "@icebergeradmin/common";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setAgentAvailable } from "@/lib/agentAvailability";

type CallCenterProps = {
  liveCalls: TLiveSession[];
  joinedLeads: TJoinedClient[];
  agentStatus?: number;
  isAgentAvailable: boolean;
  personalRoomSessionName: string;
  /** sessionName -> name of another agent currently calling that lead (voice). */
  engagedByOtherAgent: Record<string, string>;
  updateAgentStatus: (newStatus?: number) => void;
  removeWaitingRoomEntry: (entryId: string) => void;
};


const CallCenterContext = createContext<CallCenterProps>({
  liveCalls: [],
  joinedLeads: [],
  isAgentAvailable: false,
  personalRoomSessionName: "",
  engagedByOtherAgent: {},
  updateAgentStatus: () => undefined,
  removeWaitingRoomEntry: () => undefined,
});

const CallCenterProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthUser();
  const selectionReady = useAppSelector(selectSelectionReady);
  const selectedCallCenter = useAppSelector(selectSelectedCallCenter);
  const { joinedLeads, liveCalls } = useLiveSessions(selectionReady ? selectedCallCenter?.id : undefined);
  const [agentStatus, setAgentStatus] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  // "Agent" = someone who is assigned to at least one call center
  const isAgent = !!user.assignedCallCenterIds?.length

  const personalRoomSessionName =
    user?.companyUniqueName && user?.personalRoomId
      ? `${user.companyUniqueName}-${user.personalRoomId}`
      : "";

  const { waitingRoomLeads, removeEntry: removeWaitingRoomEntry } = useWaitingRoomLeads(personalRoomSessionName);
  const engagedByOtherAgent = useOtherAgentEngagements();

  const allJoinedLeads = useMemo(
    () => [...joinedLeads, ...waitingRoomLeads],
    [joinedLeads, waitingRoomLeads]
  );


  useEffect(() => {
    if (!isAgent) return setIsLoading(false);
    let mounted = true;
    getMyCurrentCallCenterStatus().then((status) => {
      if (mounted) setAgentStatus(status);
      setIsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [isAgent]);

  const updateAgentStatus = useCallback((newStatus?: number) => {
    if (newStatus === undefined) return;
    setAgentStatus((prev) => {
      if (prev === newStatus) return prev;
      notifyAgentStatusChanged(newStatus);
      return newStatus;
    });
  }, []);

  const isAgentAvailable = agentStatus === CALLCENTER_USER_STATUS_READY || agentStatus === CALLCENTER_USER_STATUS_IN_CALL;

  useEffect(() => {
    if (!isLoading) setAgentAvailable(isAgentAvailable);
  }, [isAgentAvailable, isLoading]);

  if (isLoading) return <div>Loading</div>;
  return (
    <CallCenterContext.Provider value={{ liveCalls, joinedLeads: allJoinedLeads, agentStatus, isAgentAvailable, personalRoomSessionName, engagedByOtherAgent, updateAgentStatus, removeWaitingRoomEntry }}>
      {children}
    </CallCenterContext.Provider>
  );
};

export default CallCenterProvider;

export const useCallCenterContext = () => useContext(CallCenterContext);
